import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/core/basic/Button";
import FormGroupTile from "../../../components/core/basic/FormGroupTile";
import { useFetch } from "../../../hooks/api";
import { Col, Row } from "react-bootstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { successToast, errorToast } from "../../../utils/toasts";
import { useSimpleCan } from "../../../config/casl/casl";

interface BidBudgetSettings {
  cr_ctr_lookback_window: number;
  acos_lookback_window: number;
}

interface FormData {
  crLookbackWindow: number;
  acosLookbackWindow: number;
}

const LookbackWindowConfiguration = () => {
  const { t } = useTranslation(["advertising", "common"]);
  const [formData, setFormData] = useState<FormData | null>(null);

  const queryClient = useQueryClient();
  const userCanUpdateAds = useSimpleCan("update", "Ad");

  const harvestingSettingsQuery = useFetch<BidBudgetSettings>(
    ["orgHarvestingSettings"],
    "/org_bid_budget_settings",
    {},
    {
      select: (d) => d.data,
    },
  );

  const formDataResolved = formData ?? {
    crLookbackWindow: harvestingSettingsQuery.data?.cr_ctr_lookback_window ?? 180,
    acosLookbackWindow: harvestingSettingsQuery.data?.acos_lookback_window ?? 0,
  };

  const { crLookbackWindow, acosLookbackWindow } = formDataResolved;

  const saveLookbackSettings = useMutation({
    mutationFn: async () => {
      const { crLookbackWindow, acosLookbackWindow } = formDataResolved;

      await api.put(`/org_bid_budget_settings`, {
        cr_ctr_lookback_window: crLookbackWindow,
        acos_lookback_window: acosLookbackWindow,
      });
    },

    onSuccess: async () => {
      successToast({ message: t(($) => $.advertising["Successfully Updated Lookback Window Settings"]) });
      await queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Lookback Window Settings"]), error });
    },
  });

  return (
    <Row className="w-100">
      <Col className="p-3">
        <Row className={"pt-3"}>
          <h3 className={"fs-1 ps-4 fw-bold"}>{t(($) => $.advertising["Lookback Window Settings"])}</h3>
        </Row>
        <Row className="pt-5">
          <Col xs={12} lg={9}>
            <h5 className={"m-0 ps-3 text-start fw-bold"}>{t(($) => $.advertising["ACOS Lookback Window"])}</h5>
            <div className={"ms-3 d-flex align-items-center fs-standard py-4 py-lg-1"}>
              {t(($) => $.advertising["The number of days to look back on ACOS"])}
            </div>
          </Col>
          <Col xs={12} lg={3}>
            <FormGroupTile
              defaultValue={acosLookbackWindow}
              type="formcontrol"
              formControlType={"number"}
              stateName="acosLookbackWindow"
              style={{ textAlign: "center" }}
              handleChildFormElement={(key: string, value: string) =>
                setFormData({ ...formDataResolved, [key]: parseInt(value) })
              }
              disabled={!userCanUpdateAds}
              key={1}
            />
          </Col>
        </Row>
        <Row className="pt-5">
          <Col xs={12} lg={9}>
            <h5 className={"m-0 ps-3 text-start fw-bold"}>
              {t(($) => $.advertising["Conversion Rate and Click Through Rate Lookback Window"])}
            </h5>
            <div className={"ms-3 d-flex align-items-center fs-standard py-4 py-lg-1"}>
              {t(
                ($) =>
                  $.advertising["The number of days to look back on historical click through and conversion rates"],
              )}
            </div>
          </Col>
          <Col xs={12} lg={3}>
            <FormGroupTile
              defaultValue={crLookbackWindow}
              type="formcontrol"
              formControlType={"number"}
              stateName="crLookbackWindow"
              style={{ textAlign: "center" }}
              handleChildFormElement={(key: string, value: string) => {
                setFormData({ ...formDataResolved, [key]: parseInt(value) });
              }}
              disabled={!userCanUpdateAds}
              key={2}
            />
          </Col>
        </Row>
        <Row className="pt-5">
          <Col className="d-flex justify-content-center">
            <Button onPress={() => saveLookbackSettings.mutate()} isDisabled={!userCanUpdateAds}>
              {t(($) => $.common.actions["Save"])}
            </Button>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default LookbackWindowConfiguration;
